import Scene from '../../common/core/Scene';
import Logger from '../../common/log/Logger';

export type EditorSceneDownloadFormat = 'png' | 'jpeg' | 'pdf';

type SnapshotWithFormatFn = (
  scene: Scene,
  minWidthHeight: number,
  mimeType?: string,
  quality?: number,
  globalTime?: number
) => Promise<HTMLImageElement>;

export interface EditorSceneDownloaderDeps {
  getScenes: () => Scene[];
  getSelectedScene: () => Scene;
  getWideoWidth: () => number;
  getWideoHeight: () => number;
  isDynamicProject: () => boolean;
  getCurrentTime: () => number;
  getWideoId: () => string;
  getSnapshotOfSceneWithFormat: SnapshotWithFormatFn;
  /** Optional: notify backend when a PNG/JPEG/PDF scene export completes successfully. */
  recordSceneDownload?: (format: EditorSceneDownloadFormat) => Promise<void>;
}

type ImageFormat = 'png' | 'jpeg';

interface ImageDownloadContext {
  scenes: Scene[];
  width: number;
  format: ImageFormat;
  mimeType: string;
  quality?: number;
}

interface ImageDownloadStrategy {
  canHandle: (context: ImageDownloadContext) => boolean;
  execute: (context: ImageDownloadContext) => Promise<void>;
}

export default class EditorSceneDownloader {

  constructor(private readonly deps: EditorSceneDownloaderDeps) {}

  public downloadSceneAsPng = async (): Promise<void> => {
    return this.downloadSceneAsImage('png');
  }

  public downloadSceneAsJpeg = async (): Promise<void> => {
    return this.downloadSceneAsImage('jpeg');
  }

  public downloadSceneAsPdf = async (): Promise<void> => {
    const scenes = this.deps.getScenes();
    if (scenes.length === 0) return;

    const width = this.deps.getWideoWidth();
    const height = this.deps.getWideoHeight();
    const { jsPDF } = await import('jspdf');

    if (this.deps.isDynamicProject()) {
      // VIDEO MODE: single-page PDF of the current frame at seek position
      const scene = this.deps.getSelectedScene();
      if (!scene) return;

      const image = await this.deps.getSnapshotOfSceneWithFormat(scene, width, 'image/jpeg', 0.95, this.deps.getCurrentTime());
      if (!image) return;

      const pdf = new jsPDF({
        orientation: width >= height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [width, height]
      });
      pdf.addImage(image.src, 'JPEG', 0, 0, width, height);
      pdf.save(`${this.deps.getWideoId()}.pdf`);
      await this.recordSceneDownloadTracked('pdf');
      return;
    }

    // STATIC MODE: multi-page PDF of all scenes
    const pdf = new jsPDF({
      orientation: width >= height ? 'landscape' : 'portrait',
      unit: 'px',
      format: [width, height]
    });

    let anyPdfImage = false;
    for (let i = 0; i < scenes.length; i++) {
      if (i > 0) pdf.addPage([width, height]);
      const image = await this.deps.getSnapshotOfSceneWithFormat(scenes[i], width, 'image/jpeg', 0.95);
      if (image) {
        pdf.addImage(image.src, 'JPEG', 0, 0, width, height);
        anyPdfImage = true;
      }
    }

    pdf.save(`${this.deps.getWideoId()}.pdf`);
    if (anyPdfImage) {
      await this.recordSceneDownloadTracked('pdf');
    }
  }

  private downloadSceneAsImage = async (format: ImageFormat): Promise<void> => {
    const scenes = this.deps.getScenes();
    const context: ImageDownloadContext = {
      scenes,
      width: this.deps.getWideoWidth(),
      format,
      mimeType: format === 'jpeg' ? 'image/jpeg' : 'image/png',
      quality: format === 'jpeg' ? 0.95 : undefined
    };

    const strategy = this.getImageDownloadStrategies().find((candidate) => candidate.canHandle(context));
    if (!strategy) return;
    return strategy.execute(context);
  }

  private getImageDownloadStrategies = (): ImageDownloadStrategy[] => {
    return [
      {
        canHandle: () => this.deps.isDynamicProject(),
        execute: async (context) => {
          // VIDEO MODE: export single frame at current seek position
          const scene = this.deps.getSelectedScene();
          if (!scene) return;

          const image = await this.deps.getSnapshotOfSceneWithFormat(
            scene,
            context.width,
            context.mimeType,
            context.quality,
            this.deps.getCurrentTime()
          );
          if (!image) return;

          this.downloadImage(image.src, `${this.deps.getWideoId()}.${context.format}`);
          await this.recordSceneDownloadTracked(context.format);
        }
      },
      {
        canHandle: (context) => context.scenes.length <= 1,
        execute: async (context) => {
          // STATIC MODE — single scene
          const scene = this.deps.getSelectedScene();
          if (!scene) return;

          const image = await this.deps.getSnapshotOfSceneWithFormat(
            scene,
            context.width,
            context.mimeType,
            context.quality,
            this.deps.getCurrentTime()
          );
          if (!image) return;

          this.downloadImage(image.src, `${this.deps.getWideoId()}.${context.format}`);
          await this.recordSceneDownloadTracked(context.format);
        }
      },
      {
        canHandle: () => true,
        execute: async (context) => {
          // STATIC MODE — multiple scenes: ZIP of all
          const JSZip = (await import('jszip')).default;
          const zip = new JSZip();
          let anyExported = false;

          for (let i = 0; i < context.scenes.length; i++) {
            const image = await this.deps.getSnapshotOfSceneWithFormat(
              context.scenes[i],
              context.width,
              context.mimeType,
              context.quality
            );
            if (image) {
              const response = await fetch(image.src);
              const blob = await response.blob();
              zip.file(`scene_${i + 1}.${context.format}`, blob);
              anyExported = true;
            }
          }

          if (!anyExported) return;

          const zipBlob = await zip.generateAsync({ type: 'blob' });
          const objectURL = URL.createObjectURL(zipBlob);
          this.downloadImage(objectURL, `${this.deps.getWideoId()}_scenes.zip`);
          URL.revokeObjectURL(objectURL);
          await this.recordSceneDownloadTracked(context.format);
        }
      }
    ];
  }

  private downloadImage = (url: string, filename: string): void => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private recordSceneDownloadTracked = async (format: EditorSceneDownloadFormat): Promise<void> => {
    if (!this.deps.recordSceneDownload) return;
    try {
      await this.deps.recordSceneDownload(format);
    } catch (error) {
      Logger.warn('Failed recording scene file export, reason: ' + Logger.errorToString(error));
    }
  }
}
